import { Command } from 'cmdk'
import useModal from 'src/hooks/useModal'
import { Divider, Modal as MUIModal, Paper } from '@mui/material'
import React, { useEffect, useRef } from 'react'
import CommandItem from './CmdKItem'
import cx from 'src/utils/cx'
import CmdKGroup from './CmdKGroup'
import CircularProgress from '@mui/material/CircularProgress'
import DueListModule from './modules/DueListModule'
import ComplianceModule from './modules/ComplianceModule'
import WorkOrderModule from './modules/WorkOrderModule'
import AIModule from './modules/AIModule'
import InventoryModule from './modules/InventoryModule'
import KnowledgeBaseModule from './modules/KnowledgeBaseModule'
import { useRouteName } from '@cedarjs/router'
import CmdkProductListPageModule from 'src/pages/ProductsListPage/CmdkProductListPageModule'
import { useCmdk } from './CmdkContext'
import CmdkDueListPageModule from 'src/pages/DueListPageV2Page/CmdkDueListPageModule'

export interface CommandMenuItem {
  label: string
  startIcon?: React.ReactNode
  subtext?: string
  disabled?: boolean
  onClick?: () => void
  children?: CommandMenuItem[]
  closeOnSelect?: boolean
}

export interface CommandMenuGroup {
  heading: string
  items: CommandMenuItem[]
}

interface CommandMenuProps {
  groups?: CommandMenuGroup[]
  loading?: boolean
  onClose?: () => void
  className?: string
  paperClassName?: string
  modules?: React.ComponentType<{
    search: string
    onNavigate: (groups: CommandMenuGroup[]) => void
    onBack: () => void
    pages: CommandMenuGroup[][]
    onClose: () => void
  }>[]
}

const routeMap = {
  productsList: CmdkProductListPageModule,
  dueList: CmdkDueListPageModule,
}

const CommandMenu: React.FC<CommandMenuProps> = ({
  groups = [],
  loading = false,
  onClose,
  className,
  paperClassName,
  modules = [
    DueListModule,
    ComplianceModule,
    WorkOrderModule,
    InventoryModule,
    KnowledgeBaseModule,
    AIModule,
  ],
}) => {
  const { isOpen, handleOpen, handleClose } = useModal('cmdk')
  const [search, setSearch] = React.useState('')
  const [pages, setPages] = React.useState<CommandMenuGroup[][]>([])
  const page = pages[pages.length - 1]
  const inputRef = useRef<HTMLInputElement>(null)

  const name = useRouteName()
  const RouteModule = routeMap[name as keyof typeof routeMap]
  const { data: routeData } = useCmdk()

  // Toggle the menu when ⌘K is pressed
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        if (isOpen) {
          handleClose()
        } else {
          handleOpen()
        }
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [isOpen])

  // reset the search and pages when the modal is closed
  useEffect(() => {
    if (!isOpen) {
      setPages([])
      setSearch('')
      onClose?.()
    } else {
      // Focus the input when modal opens
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    }
  }, [isOpen, onClose])

  const handleItemClick = (item: CommandMenuItem) => {
    if (item.children) {
      // If the item has children, it's a navigation item
      setPages([
        ...pages,
        [
          {
            heading: item.label,
            items: item.children,
          },
        ],
      ])
      setSearch('')
    } else {
      item.onClick?.()
      if (item.closeOnSelect !== false) {
        handleClose()
      }
    }
  }

  const handleNavigate = (groups: CommandMenuGroup[]) => {
    setPages([...pages, groups])
    setSearch('')
  }

  const handleBack = () => {
    setPages((pages) => pages.slice(0, -1))
  }

  return (
    <MUIModal
      open={isOpen}
      onClose={handleClose}
      className={cx('flex items-center justify-center', className)}
      disableAutoFocus
    >
      <Paper
        className={cx('flex h-2/3 w-1/2 flex-col rounded-lg', paperClassName)}
      >
        <Command
          shouldFilter={false}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !search) {
              e.preventDefault()
              handleBack()
            }
          }}
          className="flex h-full flex-col"
        >
          <Command.Input
            value={search}
            ref={inputRef}
            onValueChange={setSearch}
            placeholder="Search..."
            className="bg-background w-full rounded-t-lg border-none px-4 py-2 font-sans text-[15px] text-gray-900 outline-none placeholder:text-gray-500"
          />
          <Divider />
          <Command.List className="overflow-auto overscroll-contain px-1 pb-1 transition transition-[height] duration-100 ease-in-out">
            {loading && (
              <Command.Loading>
                <div className="flex h-8 items-center justify-center">
                  <CircularProgress size={16} className="mr-2 text-gray-500" />
                  <span className="text-gray-500">Hang on…</span>
                </div>
              </Command.Loading>
            )}
            <Command.Empty className="flex h-8 items-center justify-center text-gray-500">
              No results found.
            </Command.Empty>

            {/* Static menu groups */}
            {(page || groups).map((group, groupIndex) => (
              <CmdKGroup key={groupIndex} heading={group.heading}>
                {group.items.map((item, itemIndex) => (
                  <CommandItem
                    key={itemIndex}
                    onSelect={() => handleItemClick(item)}
                    disabled={item.disabled}
                    subtext={item.subtext}
                    startIcon={item.startIcon}
                  >
                    {item.label}
                  </CommandItem>
                ))}
              </CmdKGroup>
            ))}

            {RouteModule && (
              <RouteModule
                onNavigate={handleNavigate}
                onBack={handleBack}
                onClose={handleClose}
                pages={pages}
                search={search}
                {...(routeData as any)}
              />
            )}
            {/* Dynamic modules */}
            {modules.map((Module, index) => (
              <Module
                key={index}
                search={search}
                onNavigate={handleNavigate}
                onBack={handleBack}
                onClose={handleClose}
                pages={pages}
              />
            ))}
          </Command.List>
        </Command>
      </Paper>
    </MUIModal>
  )
}

export default CommandMenu
